import type { MarginTotals } from "../utils/channelMargin";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";

interface Props {
  months: string[];
  margins: Map<string, MarginTotals>;
  isPastMonth: (iso: string) => boolean;
  monthDividers: boolean;
  top: number;
}

const TITLE = "Gross margin from actual sales.";

// Sticky "Gross margin" header row under the month revenue totals, on the
// same basis as the channel GM% summary: past months only. The current and
// future months, and a month with no revenue, stay blank.
export default function MonthMarginHeaderRow({ months, margins, isPastMonth, monthDividers, top }: Props) {
  // -1px = Revenue/Gross margin boundary, +1px = header/body boundary.
  const separators = "0 -1px 0 0 var(--border), 0 1px 0 0 var(--border)";

  return (
    <tr style={{ background: "var(--muted)" }}>
      <th
        style={{ background: "var(--muted)", color: "var(--foreground)", borderColor: "var(--border)", boxShadow: separators, top, width: PLAN_PRODUCT_COL_WIDTH, maxWidth: PLAN_PRODUCT_COL_WIDTH }}
        className="sticky left-0 z-30 text-left px-3 py-1.5 font-semibold whitespace-nowrap border-r"
      >
        Gross margin
      </th>
      {months.map((m) => {
        const past = isPastMonth(m);
        const gmPct = margins.get(m)?.gmPct ?? null;
        return (
          <th
            key={m}
            style={{
              background: past ? "var(--border)" : "var(--muted)",
              color: "var(--foreground)",
              boxShadow: separators,
              top,
              ...(monthDividers ? { borderColor: "var(--accent)" } : {}),
            }}
            className={`sticky z-20 px-2 py-1.5 text-right font-normal tabular-nums whitespace-nowrap${monthDividers ? " border-r" : ""}`}
            title={gmPct == null ? undefined : TITLE}
          >
            {gmPct != null && `${(gmPct * 100).toFixed(1)}%`}
          </th>
        );
      })}
    </tr>
  );
}
